import { useTranslation } from 'react-i18next';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useIsMobileBreakpoint } from '@/hooks/useIsBreakpoint';
import { queryClient } from '@/lib';
import type { RecurrenceOption } from '@/pages/Schedule/components';
import { useScheduleContext } from '@/pages/Schedule/contexts';
import {
  useDeleteEntryMessage,
  useUpdateEntryHandlers,
} from '@/pages/Schedule/hooks';
import labelIcon from '@/pages/Schedule/icons/label.png';
import whoIcon from '@/pages/Schedule/icons/who.png';
import { useDraftEntryStore } from '@/pages/Schedule/stores';
import type { DecoratedEntry } from '@/pages/Schedule/types';
import { LabelType } from '@/types/gql.generated';
import { Flex, Input, Modal, ModalOverlay, VStack, useDisclosure } from '@/ui';
import { createEntryMessagesQueryKey } from '@/utils/queryKeys';
import { isPollSchedule, supportsAddons } from '@/utils/schedule';
import { CategorySheet } from '../../Category/components/CategorySheet';
import { EntryModalSidebar, EntryModalForm } from '../components';
import { DescriptionInput } from '../components/DescriptionInput';
import { EmojiSheet } from '../components/EmojiSheet';
import { EntryModalBody } from '../components/EntryModalBody';
import { EntryModalContent } from '../components/EntryModalContent';
import { EntryModalHeader } from '../components/EntryModalHeader';
import { EntryModalRsvpBar } from '../components/EntryModalRsvpBar';
import { LabelSheet } from '../components/LabelSheet';
import { LocationSheet } from '../components/LocationSheet';
import { MessagesButton } from '../components/MessagesButton';
import { MoreMenu } from '../components/MoreMenu';
import { RsvpButton } from '../components/RsvpButton';
import { TitleInput } from '../components/TitleInput';
import { WhenSheet } from '../components/WhenSheet';
import { MODAL_WIDTH } from '../constants';
import { useFetchEntryMessages, useAddFreqWithMessageWarning } from '../hooks';
import { useRenderModalOverlay } from '../hooks/useRenderModalOverlay';
import type { EntryModalResult } from '../types';

type Props = {
  onClose: (result: EntryModalResult, entry: DecoratedEntry) => void;
  onSave: (entry: DecoratedEntry) => Promise<void>;
};

export const WriteableEntryModal = ({ onSave, onClose }: Props) => {
  const { t } = useTranslation('entryModal');
  const isMobileBreakpoint = useIsMobileBreakpoint();
  const { scheduleId, schedule } = useScheduleContext();
  const { value: rsvpEnabled } = useFeatureFlag('rsvpEnabled');
  const { renderModalOverlay } = useRenderModalOverlay();
  const isPoll = Boolean(schedule && isPollSchedule(schedule));

  const {
    isOpen: isAddFreqWithMessageWarningOpen,
    open: openAddFreqWithMessageWarning,
    confirm: confirmAddFreqWithMessageWarning,
    cancel: cancelAddFreqWithMessageWarning,
  } = useAddFreqWithMessageWarning();

  const { draftTouched, entry, instance, draftMode, updateDraftEntry } =
    useDraftEntryStore((state) => ({
      draftTouched: state.draftTouched,
      entry: state.draftEntry!, // Modal can't be mounted without draftEntry
      instance: state.draftInstance,
      draftMode: state.draftMode,
      cloneDraftEntry: state.cloneDraftEntry,
      updateDraftEntry: state.updateDraftEntry,
    }));

  const { bulkDeleteEntryMessages } = useDeleteEntryMessage(entry.id);

  const {
    frequency,
    interval,
    byWeekday,
    untilDate,
    onStartDateChange,
    onEndDateChange,
    onStartTimeChange,
    onEndTimeChange,
    onAddTime,
    onRemoveTime,
    onUntilDateChange,
    onRecurrenceChange,
    onMonthChange,
    onWeekdayChange,
    onTitleChange,
    onEmojiChange,
    onCategoryChange,
    onLabelsChange,
    onWhoLabelsChange,
    onLocationChange,
    onDescriptionChange,
  } = useUpdateEntryHandlers(entry, updateDraftEntry);

  const { startDate, endDate, isOnDay, rule } = entry.recurrences[0];

  const {
    isLoading: isLoadingExistingMessages,
    messages,
    messagesForInstance,
  } = useFetchEntryMessages();

  const {
    isOpen: isCategorySheetOpen,
    onOpen: openCategorySheet,
    onClose: closeCategorySheet,
  } = useDisclosure();

  const {
    isOpen: isLabelSheetOpen,
    onOpen: openLabelSheet,
    onClose: closeLabelSheet,
  } = useDisclosure();

  const {
    isOpen: isWhoLabelSheetOpen,
    onOpen: openWhoLabelSheet,
    onClose: closeWhoLabelSheet,
  } = useDisclosure();

  const closeModal = (result: EntryModalResult = 'cancel') => {
    onClose(result, entry);
  };

  const handleDeleteEntry = () => {
    const result = draftMode === 'create' ? 'cancel' : 'delete-entry';
    closeModal(result);
  };

  const handleDeleteInstance = () => {
    const result = draftMode === 'create' ? 'cancel' : 'delete-instance';
    closeModal(result);
  };

  const saveIfTouched = () => {
    if (draftTouched) {
      closeModal('save');
    } else {
      closeModal('cancel');
    }
  };

  const handleRecurrenceChange = (option: RecurrenceOption) => {
    // Confirm if moving from once -> recurring with messages
    if (
      frequency === null &&
      option.rule.freq !== null &&
      messagesForInstance.length > 0
    ) {
      openAddFreqWithMessageWarning(async () => {
        await bulkDeleteEntryMessages(
          messagesForInstance.map((message) => message.id)
        );
        await queryClient.invalidateQueries({
          queryKey: createEntryMessagesQueryKey(scheduleId, entry.id),
        });
        onRecurrenceChange(option);
      });
    } else {
      onRecurrenceChange(option);
    }
  };

  if (!schedule) {
    return null;
  }

  return (
    <>
      <ConfirmDialog
        confirmButtonText={t('add_freq_warning.confirm')}
        dialogOverlayProps={{ zIndex: 9999 }}
        isOpen={isAddFreqWithMessageWarningOpen}
        title={t('add_freq_warning.title')}
        onCancel={cancelAddFreqWithMessageWarning}
        onConfirm={confirmAddFreqWithMessageWarning}
      />

      <Modal
        isCentered
        isOpen
        // `returnFocusOnClose`` is disabled because we can't guarantee that the element that
        // opened the modal will still be focusable when the modal closes. For example, this
        // modal can be opened from inside a `Menu` component on the agenda view and the menu
        // closes as soon as the menu item is clicked. Chakra is unable to return focus to the
        // menu item and the scroll position of the page is reset to the top.
        // https://github.com/chakra-ui/chakra-ui/issues/7734
        returnFocusOnClose={false}
        scrollBehavior="outside"
        closeOnOverlayClick={
          !isCategorySheetOpen && !isLabelSheetOpen && !isWhoLabelSheetOpen
        }
        onClose={saveIfTouched}
      >
        {renderModalOverlay && <ModalOverlay />}

        <EntryModalContent data-testid="entry-modal">
          <Flex
            direction="column"
            justify="space-between"
            w={MODAL_WIDTH + 'px'}
          >
            <EntryModalForm onSubmit={() => closeModal('save')}>
              <EntryModalHeader
                onCancelClick={() => closeModal('cancel')}
                onSaveClick={() => closeModal('save')}
              >
                {supportsAddons(schedule) && (
                  <>
                    {rsvpEnabled && <RsvpButton />}
                    <MessagesButton
                      hasMessages={messagesForInstance.length > 0}
                      isDisabled={isLoadingExistingMessages}
                    />
                  </>
                )}

                <MoreMenu
                  entry={entry}
                  instance={instance}
                  onDeleteEntry={handleDeleteEntry}
                  onDeleteInstance={handleDeleteInstance}
                />
              </EntryModalHeader>

              <EntryModalBody>
                <EmojiSheet
                  mb="2"
                  ml="-12px"
                  value={entry.emoji}
                  onSelect={onEmojiChange}
                />

                <TitleInput
                  autoFocus={!isMobileBreakpoint}
                  fontSize="2xl"
                  id="title"
                  lineHeight="1"
                  placeholder={t('title.placeholder')}
                  value={entry.title ?? ''}
                  onChange={onTitleChange}
                  onEnterPress={saveIfTouched}
                />

                <VStack
                  alignItems="start"
                  display="flex"
                  mb="8"
                  mt="8"
                  spacing="2"
                >
                  <WhenSheet
                    byWeekday={byWeekday}
                    endDate={endDate}
                    frequency={frequency}
                    hasAnyMessages={messages.length > 0}
                    interval={interval}
                    isOnDay={isOnDay}
                    rsvpEnabled={entry.rsvpSettings.enabled}
                    rule={rule}
                    startDate={startDate}
                    untilDate={untilDate}
                    onAddTimeClick={onAddTime}
                    onEndDateChange={onEndDateChange}
                    onEndTimeChange={onEndTimeChange}
                    onMonthChange={onMonthChange}
                    onRecurrenceChange={handleRecurrenceChange}
                    onRemoveTimeClick={onRemoveTime}
                    onStartDateChange={onStartDateChange}
                    onStartTimeChange={onStartTimeChange}
                    onUntilDateChange={onUntilDateChange}
                    onWeekdayChange={onWeekdayChange}
                  />

                  {!isPoll && (
                    <>
                      <CategorySheet
                        isOpen={isCategorySheetOpen}
                        selected={entry.category}
                        onClose={closeCategorySheet}
                        onOpen={openCategorySheet}
                        onSelect={onCategoryChange}
                      />

                      <LabelSheet
                        isOpen={isWhoLabelSheetOpen}
                        labelType={LabelType.Who}
                        labels={entry.whoLabels}
                        placeholder={t('who.placeholder')}
                        icon={
                          <img
                            alt=""
                            height="18px"
                            src={whoIcon}
                            width="18px"
                          />
                        }
                        onChange={onWhoLabelsChange}
                        onClose={closeWhoLabelSheet}
                        onOpen={openWhoLabelSheet}
                      />

                      <LabelSheet
                        isOpen={isLabelSheetOpen}
                        labelType={LabelType.Default}
                        labels={entry.labels}
                        placeholder={t('label.placeholder')}
                        icon={
                          <img
                            alt=""
                            height="18px"
                            src={labelIcon}
                            width="18px"
                          />
                        }
                        onChange={onLabelsChange}
                        onClose={closeLabelSheet}
                        onOpen={openLabelSheet}
                      />
                    </>
                  )}

                  <LocationSheet
                    googlePlaceId={entry.locationWithPlace?.googlePlaceId}
                    value={entry.locationWithPlace?.name ?? ''}
                    onChange={onLocationChange}
                  />
                </VStack>

                <DescriptionInput
                  value={entry.description ?? ''}
                  onChange={onDescriptionChange}
                />
                <Input display="none" type="submit" />
              </EntryModalBody>
            </EntryModalForm>

            <EntryModalRsvpBar />
          </Flex>

          {supportsAddons(schedule) && (
            <EntryModalSidebar
              messages={messagesForInstance}
              onSave={() => onSave(entry)}
            />
          )}
        </EntryModalContent>
      </Modal>
    </>
  );
};
